import React from 'react'
import ClearInputButton from '../../ui/ClearInputButton'
import styles from './styles.module.scss'

interface TextInputProps {
    label: string
    value: string
    onChange: (value: string) => void
    onBlur?: () => void
    placeholder?: string
    type?: 'text' | 'email' | 'tel' | 'number' | 'date'
    autocomplete?: string
    // Defaults to the autocomplete hint (e.g. "address-line1"), which browsers use to match saved details
    name?: string
    required?: boolean
    id?: string
    defaultDate?: string
    error?: string
    hint?: string
    maxLength?: number
    className?: string
}

const TextInput: React.FC<TextInputProps> = ({
    label,
    value,
    onChange,
    onBlur,
    placeholder,
    type = 'text',
    autocomplete,
    name,
    required = false,
    id,
    defaultDate,
    error,
    hint,
    maxLength,
    className,
}) => {
    const inputId = id || label.toLowerCase().replace(/\s+/g, '-')
    const [tempValue, setTempValue] = React.useState(value)
    const [focused, setFocused] = React.useState(false)
    const inputRef = React.useRef<HTMLInputElement>(null)

    const handleFocus = () => {
        setFocused(true)
        if (type === 'date' && value === '' && defaultDate) {
            setTempValue(defaultDate)
        }
    }

    const handleBlur = () => {
        setFocused(false)
        if (type === 'date' && tempValue === defaultDate && value === '') {
            setTempValue('')
        }
        if (onBlur) {
            onBlur()
        }
    }

    // Native date inputs always show their own format text, so keep the label up
    const labelRaised = focused || value !== '' || type === 'date'

    return (
        <div className={styles.fieldWrapper}>
            <div className={styles.control}>
                <input
                    ref={inputRef}
                    id={inputId}
                    type={type}
                    value={type === 'date' && value === '' ? tempValue : value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    className={`${styles.input} ${error ? styles.inputError : ''} ${className || ''}`}
                    name={name ?? autocomplete}
                    autoComplete={autocomplete}
                    required={required}
                    aria-required={required}
                    maxLength={maxLength}
                />
                <label className={`${styles.label} ${labelRaised ? styles.labelRaised : ''}`} htmlFor={inputId}>
                    {label}
                </label>
                {value !== '' && type !== 'date' && (
                    <ClearInputButton
                        label={label}
                        onClear={() => {
                            onChange('')
                            inputRef.current?.focus()
                        }}
                    />
                )}
            </div>
            {error && <span className={styles.error}>{error}</span>}
            {hint && <span className={styles.hint}>{hint}</span>}
        </div>
    )
}

export default TextInput
